import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Card,
  CardContent,
  FormControl,
  Divider,
  CircularProgress,
  Chip,
  Autocomplete,
  TextField,
} from "@mui/material";
import ThemeSelect from "@/ui/fields/Select";

import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { FiberManualRecord as DotIcon } from "@mui/icons-material";
import {
  customerEndpoints,
  onboardingEndpoints,
  sharePageEndpoints,
} from "@/shared/api/endpoints";
import {
  AdditionalSupportEntry,
  ApiResponse,
  CalculatedTotals,
  CustomerSharingResponse,
  Debt,
  DigitalLender,
  LenderNotShared,
  NotifyCustomerDebtAdvice,
  NotifyCustomerDebtAdviceResponse,
  ServiceResponse,
  SharedDocResponse,
  TotalEnergyBills,
} from "@/shared/api/types";
import { httpGet, httpPost, httpPut } from "@/shared/api/http";
import femaCapture from "@/shared/fema/capture";
import { BenefitsService, OnboardingService } from "@/shared/api/services";
import { getCustomerId } from "@/shared/lib/utils/getCustomerId";
import { SharePageService } from "@/shared/api/services/sharePageService";
import type { LenderParentRecord } from "@/shared/api/services/lenderService";
import { formatDate } from "@/shared/lib/utils/formatDate";
import {
  coerceConsentToHuman,
  isConsentAffirmative,
} from "@/shared/lib/utils/consent";
import { useAlert } from "@/app/providers/AlertProvider";
import DependentsService, {
  DependentDobRecord,
} from "@/shared/api/services/dependentsService";
import { ThemeColours } from "@/clients/acme/config/colours";
import { calculateAge } from "@/shared/lib/utils/others";
import { getCompanyDocs } from "@/shared/share/sharePageUtils";

import type { Company, Document } from "./SharePage";

function isOver65(dobStr?: string): boolean {
  const ageYears = calculateAge(dobStr);
  return typeof ageYears === "number" && ageYears >= 65;
}

function buildQueryString(
  params: { param: string; value: string | number }[],
): string {
  const query = params
    .map(
      ({ param, value }) =>
        `${encodeURIComponent(param)}=${encodeURIComponent(value)}`,
    )
    .join("&");
  return query ? `?${query}` : "";
}

interface AdditionalSupportModalProps {
  onClose: () => void;
  digitalData: Object | undefined;
  ieMstrLenderData: LenderParentRecord[];
  digitalLenders: DigitalLender[];
  summary: CalculatedTotals;
  setCompanies: React.Dispatch<React.SetStateAction<Company[]>>;
  companies: Company[];
  setCompanyShareLastUpdateDate: React.Dispatch<React.SetStateAction<number>>;
  setFreePostLastUpdateDate: React.Dispatch<React.SetStateAction<number>>;
  freepostCreditorNamesList: DigitalLender[];
}

const AdditionalSupportModal: React.FC<AdditionalSupportModalProps> = ({
  onClose,
  digitalData,
  ieMstrLenderData,
  digitalLenders,
  summary,
  setCompanies,
  companies,
  setCompanyShareLastUpdateDate,
  setFreePostLastUpdateDate,
  freepostCreditorNamesList,
}) => {
  const [loadedBenefitsAmount, setLoadedBenefitsAmount] = useState(false);
  const [showENWMessage, setShowENWMessage] = useState(false);
  const [showUKPNMessage, setShowUKPNMessage] = useState(false);

  // MWG state vars
  const [disableMwgBtns, setDisableMwgBtns] = useState(false);
  const [count, setCount] = useState(0);
  const [visible, setVisible] = useState(false);

  const { showAlert } = useAlert();

  const [postcodeRegion, setPostcodeRegion] = useState("");
  const [
    showWestViewAdviceAndResourceCentre,
    setShowWestViewAdviceAndResourceCentre,
  ] = useState(false);
  const [isCitizensAdviceMidLincolnshire, setIsCitizensAdviceMidLincolnshire] =
    useState(false);
  const [isWellnessRelatedPostCode, setIsWellnessRelatedPostCode] =
    useState(false);
  const [isUUWellnessRelatedPostCode, setIsUUWellnessRelatedPostCode] =
    useState(false);

  const [isSLSEligible, setIsSLSEligible] = useState(false);
  const [showCitizensAdvicePlymouth, setShowCitizensAdvicePlymouth] =
    useState(false);
  const [hasMobileNumber, setHasMobileNumber] = useState(false);
  const [supportPartnersNames, setSupportPartnersNames] = useState<string[]>([
    "",
  ]);
  const [companyArr, setCompanyArr] = useState<string[]>([""]);

  const [selectedSupportPartners, setSelectedSupportPartners] = useState<
    string[]
  >([]);

  const [customerShareData, setCustomerShareData] = useState<
    CustomerSharingResponse[]
  >([
    {
      shareAgain: false,
      lastUpdatedBy: 0,
      lastUpdateDate: "",
      subDomain: "",
      isDelete: false,
      hasCreditorPortal: "",
      accountNumber: "",
      creationDate: "",
      schemaName: "",
      styleClass: "",
      isSharing: "",
      isEditable: false,
      createdBy: 0,
      lenderName: "",
      customerId: 0,
      revokeBtn: false,
      creditor: "",
      id: 0,
      shareNow: "",
      lenderOrgId: 0,
    },
  ]);

  const [
    hasVulnerabilityRegistrationService,
    setHasVulnerabilityRegistrationService,
  ] = useState(false);

  const [isNationalDebtlineMWN, setIsNationalDebtlineMWN] = useState(false);
  const [creditorNamesList, setCreditorNamesList] = useState<[]>([]);
  const [isOfferMoneyWellness, setIsOfferMoneyWellness] = useState(false);
  const [showYesEnergy, setShowYesEnergy] = useState(false);
  const [showYWCTMessage, setShowYWCTMessage] = useState(false);
  const [showMLBCard, setShowMLBCard] = useState(false);
  const [showIncomeMaxCard, setShowIncomeMaxCard] = useState(false);
  const [shouldShowPayPlan, setShouldShowPayPlan] = useState(false);
  const [isCadentEligible, setIsCadentEligible] = useState(false);

  const [isLoadedWater, setIsLoadedWater] = useState(false);
  const [isLoadedPcode, setIsLoadedPcode] = useState(false);
  const [disableContinue, setDisableContinue] = useState(false);

  const [waterSupportEntitlement, setWaterSupportEntitlement] = useState("");
  const [waterSureEntitlement, setwaterSureEntitlement] = useState("");
  const [waterCompany, setWaterCompany] = useState("");
  const [extraMonthlyBenefitsAmount, setExtraMonthlyBenefitsAmount] =
    useState(null);

  const [additionalSupportCompanies, setAdditionalSupportCompanies] = useState<
    AdditionalSupportEntry[]
  >([
    {
      companyName: "",
      customerId: 0,
    },
  ]);

  const [selectedMlb, setSelectedMlb] = useState("U");
  const [selectedIm, setSelectedIm] = useState("U");
  const [selectedVrs, setSelectedVrs] = useState("U");
  const [selectedSls, setSelectedSls] = useState("U");
  const [selectedNdl, setSelectedNdl] = useState("U");
  const [selectedPp, setSelectedPp] = useState("U");
  const [selectedCg, setSelectedCg] = useState("U");
  const [selectedYe, setSelectedYe] = useState("U");
  const [selectedYwct, setSelectedYwct] = useState("U");

  const [totalEnergyBills, setTotalEnergyBills] = useState<TotalEnergyBills>({
    totalDebtRepayEnergyBills: 0,
    totalExpEnergyBills: 0,
  });
  const [debts, setDebts] = useState<[]>([]);
  const [loadedDebts, setLoadedDebts] = useState<boolean>(false);
  const [isAtBottom, setIsAtBottom] = useState<boolean>(false);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [dependantDobData, setDependantDobData] = useState<
    DependentDobRecord[]
  >([]);
  const [dependantDobDataLoaded, setDependantDobDataLoaded] =
    useState<boolean>(false);

  const checkScrollBottom = () => {
    const el = contentRef.current;
    if (el) {
      const scrollPosition = el.scrollTop + el.clientHeight;
      const atBottom = scrollPosition >= el.scrollHeight - 1;
      setIsAtBottom(atBottom);
    }
  };

  // GET dependent DOB data
  useEffect(() => {
    async function getDependantData() {
      const customerData = JSON.parse(
        localStorage.getItem("customerData") as string,
      );

      const dependentsResponse = await DependentsService.getDependents(
        customerData.customerId as number,
      );

      if (dependentsResponse.success && dependentsResponse.data) {
        setDependantDobData(dependentsResponse.data);
        setDependantDobDataLoaded(true);
      }
    }

    getDependantData();
  }, []);

  useEffect(() => {
    let timer: string | number | NodeJS.Timeout | undefined;
    if (visible && count > 0) {
      timer = setInterval(() => {
        setCount((prev) => prev - 1);
      }, 1000);
    } else if (count === 0) {
      setVisible(false);
      setDisableMwgBtns(false);
    }

    return () => clearInterval(timer);
  }, [count, visible]);

  useEffect(() => {
    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    if (customerData.mobileNumber) {
      setHasMobileNumber(true);
    }
  }, []);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    el.addEventListener("scroll", checkScrollBottom);

    setTimeout(checkScrollBottom, 0);

    return () => {
      el.removeEventListener("scroll", checkScrollBottom);
    };
  }, []);

  function buildCompanyData(companyName: string) {
    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    const lenderRaw =
      ieMstrLenderData.find((item) => item.raw.description == companyName)
        ?.raw ?? null;

    return {
      lender: lenderRaw?.lender ?? companyName,
      schemaName: lenderRaw?.schemaName,
      subDomain: lenderRaw?.subDomain,
      accountNumber: customerData.emailAddress as string,
    };
  }

  const handleShareCompany = async (choice: string, company: string) => {
    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    const useStateMap: Record<string, (...args: any[]) => void> = {
      mlb: setSelectedMlb,
      im: setSelectedIm,
      vrs: setSelectedVrs,
      sls: setSelectedSls,
      ndl: setSelectedNdl,
      pp: setSelectedPp,
      cg: setSelectedCg,
      ye: setSelectedYe,
      ywct: setSelectedYwct,
    };

    const lenderNameMap: Record<string, string> = {
      mlb: "Make Life Better",
      im: "Income Max",
      vrs: "Vulnerability Registration Service",
      sls: "Stop Loan Sharks",
      ndl: "National Debtline",
      pp: "PayPlan",
      cg: "Cadent Gas",
      ye: "YES Energy Solutions",
      ywct: "Yorkshire Water Community Trust",
    };

    const companyName: string = lenderNameMap[company] || "";

    useStateMap[company]?.(choice);

    const shareEntriesForCompany = customerShareData.filter(
      (item) => item.lenderName === companyName && item.isDeleted != "Y",
    );

    const isDisabled = companies.filter(
      (c) =>
        c.disableShare &&
        c.raw?.isDeleted != "Y" &&
        c.raw?.isSharing != "N" &&
        c.raw?.lenderName == companyName,
    );

    if (isDisabled.length > 0) {
      return;
    }

    // We need to create an entry
    if (shareEntriesForCompany.length == 0) {
      if (choice == "Y") {
        const sharingCompany = buildCompanyData(companyName);

        // Create new share entry
        try {
          const payload = {
            accountNumber: sharingCompany.accountNumber,
            customerId: customerData.customerId,
            isSharing: "Y",
            lenderName: sharingCompany.lender,
            schemaName: sharingCompany.schemaName,
            subDomain: sharingCompany.subDomain,
            shareNow: choice,
            source: "additional-support",
          };

          const response = await httpPost<CustomerSharingResponse>(
            sharePageEndpoints.createCustomerSharing(),
            payload,
          );

          setCompanyShareLastUpdateDate(
            new Date(response.data.lastUpdateDate).getTime(),
          );

          // To prevent duplicate requests
          setCustomerShareData([...customerShareData, response.data]);

          const docsResp = await getCompanyDocs(
            String(response.data.id),
            response.data.lenderName,
            response.data.accountNumber,
          );
          const docsArray: SharedDocResponse[] = Array.isArray(docsResp)
            ? (docsResp as SharedDocResponse[])
            : (docsResp?.data ?? []);
          const documents: Document[] = docsArray.map(
            (doc: SharedDocResponse) => {
              return {
                id: String(doc.id),
                name: doc.documentName || doc.fileName,
                size: doc.documentSize,
                type: "wage-slip",
                uploadedDate: formatDate(doc.lastUpdateDate),
                raw: doc,
              };
            },
          );

          const obj = {
            declarationData: undefined,
            id: parseInt(response.data.id),
            name: sharingCompany.lender,
            reference: sharingCompany.accountNumber,
            type: "all",
            address: "",
            documents,
            selectedDocuments: [],
            lastShared: "",
            raw: response.data,
            isFp: false,
          };

          if (sharingCompany.lender == "National Debtline") {
            obj.ndlData = {
              debtAdviceContactPreferenceNdl: "Y",
              emailMeNdl: false,
              textMeNdl: true,
            };
          }

          setCompanies((prev) => [...prev, obj]);
        } catch (error) {
          femaCapture("creating_share_company_data_error", error, {
            category: "api",
            severity: "error",
            skipSanitization: false,
            user: {},
          });
          throw error;
        }
      }

      return;
    }

    // There is already an entry, update existing ones
    for (const entry of shareEntriesForCompany) {
      const companyEntry = companies.find((c) => c.id === entry.id);
      const disableShare = Boolean(companyEntry?.disableShare);

      const payload = {
        ...entry,
        shareNow: disableShare ? "N" : choice,
        isSharing: "Y",
      };

      const { data } = await httpPut<CustomerSharingResponse>(
        sharePageEndpoints.createCustomerSharing(),
        payload,
      );

      const newShareData = customerShareData.map((entry) => {
        if (entry.id == data.id) {
          entry = data;
        }

        return entry;
      });

      setCustomerShareData(newShareData);

      setCompanies((prev) => {
        return prev.map((c) => {
          return c.id === data.id
            ? {
                ...c,
                raw: data,
              }
            : c;
        });
      });
    }
  };

  async function sendWaterCompany(): Promise<undefined | string> {
    try {
      let skip = false;

      for (const c of companies) {
        if (c.name == waterCompany && c.raw?.isDeleted != "Y") {
          skip = true;
          break;
        }
      }

      const isDigital = creditorNamesList.some(
        (e: any) => e.creditor == waterCompany,
      );

      if (skip) {
        return;
      }

      if (isDigital) {
        return "is-digital";
      }
      const customerData = JSON.parse(
        localStorage.getItem("customerData") as string,
      );

      if (waterSupportEntitlement != "Y" && waterSureEntitlement != "Y") {
        return;
      }

      const company = freepostCreditorNamesList.find((entry: DigitalLender) =>
        entry.creditor == waterCompany,
      );

      if (!company) {
        return;
      }

      let coveringNotes = `I am in financial difficulty and because of this I have used IE Hub to review my budget. I have enclosed a copy for your record. You will be able to see from this information that I’m unable to make my full contractual payments to you.`;

      if (customerData?.mobileNumber) {
        coveringNotes = `I am in financial difficulty and because of this I have used IE Hub to review my budget. I have enclosed a copy for your record. You will be able to see from this information that I'm unable to make my full contractual payments to you. Please call me on ${customerData.mobileNumber} so we can discuss my options.`;
      }

      const payload = {
        companyName: waterCompany,
        referenceNumber: customerData.emailAddress,
        address: company?.creditorAddress,
        notes: coveringNotes,
        isTcAccepted: "Y",
        customerId: customerData.customerId,
        userId: customerData.userId,
        isApiCall: "Y",
        isSharing: "Y",
        shareNow: "N",
        source: "additional-support",
      };

      const response = await httpPost<CustomerSharingResponse>(
        sharePageEndpoints.createFreepostSharing(),
        payload,
      );

      setCompanies((prev) => [
        ...prev,
        {
          declarationData: undefined,
          id: String(payload.referenceNumber),
          name: payload.companyName,
          reference: String(payload.referenceNumber),
          type: "all",
          address: company?.creditorAddress ?? "",
          documents: [],
          selectedDocuments: [],
          lastShared: "",
          raw: response.data,
          isFp: true,
        },
      ]);

      setFreePostLastUpdateDate((prev) => Math.max(prev, Date.now()));
    } catch (e) {
      console.error(e);
    }
  }

  async function sendSupportCompanies() {
    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    const companiesList: string[] = [];
    let shareNowMap: Record<string, string> = {
      "Electricity North West": "N",
      "Yorkshire Water Community Trust": "N",
      "United Utilities": "N",
    };

    const over65 = isOver65(customerData.debtor1DateOfBirthStr);
    const dependantOver5 =
      (await hasDependantOver5()) ||
      (customerData.dependentChildrenAged16Plus &&
        customerData.dependentChildrenAged16Plus != 0);
    const dependantUnder5 = await hasDependantUnder5();

    const hasVulnerability =
      customerData.field3TxtFld && customerData.field3TxtFld !== "N";

    const enwCondition =
      over65 || (!dependantOver5 && dependantUnder5) || hasVulnerability;

    if (enwCondition) {
      shareNowMap["Electricity North West"] = "Y";
    }

    const digitalStatus = await sendWaterCompany();

    // electric company
    const electricCompanyName = showENWMessage
      ? "Electricity North West"
      : showUKPNMessage
        ? "UK Power Networks"
        : "";

    if (electricCompanyName) {
      companiesList.push(electricCompanyName);
    }

    if (isCitizensAdviceMidLincolnshire) {
      companiesList.push("Citizens Advice Mid Lincolnshire");
    }

    if (showCitizensAdvicePlymouth) {
      companiesList.push("Citizens Advice Plymouth");
    }

    if (showWestViewAdviceAndResourceCentre) {
      companiesList.push("West View Advice and Resource Centre");
    }

    if (showYesEnergy) {
      companiesList.push("YES Energy Solutions");
    }

    if (showYWCTMessage) {
      companiesList.push("Yorkshire Water Community Trust");
    }

    if (
      digitalStatus == "is-digital" &&
      (waterSupportEntitlement == "Y" || waterSureEntitlement == "Y")
    ) {
      companiesList.push(waterCompany);
    }

    const resolvedCustomerId: string | number | null = await getCustomerId();

    for (const company of companiesList) {
      let skip = false;
      for (const c of companies) {
        if (
          c.name == company &&
          c.raw?.isDeleted != "Y" &&
          c.raw?.isSharing != "N"
        ) {
          skip = true;
          break;
        }
      }

      if (skip) {
        continue;
      }

      const sharingCompany = buildCompanyData(company);

      try {
        const payload = {
          accountNumber: sharingCompany.accountNumber,
          customerId: resolvedCustomerId,
          isSharing: "Y",
          lenderName: sharingCompany.lender,
          schemaName: sharingCompany.schemaName,
          subDomain: sharingCompany.subDomain,
          shareNow: shareNowMap[company] || "Y",
          source: "additional-support",
        };

        const response = await httpPost<CustomerSharingResponse>(
          sharePageEndpoints.createCustomerSharing(),
          payload,
        );

        setCompanyShareLastUpdateDate(
          new Date(response.data.lastUpdateDate).getTime(),
        );

        const docsResp = await getCompanyDocs(
          String(response.data.id),
          response.data.lenderName,
          response.data.accountNumber,
        );
        const docsArray: SharedDocResponse[] = Array.isArray(docsResp)
          ? (docsResp as SharedDocResponse[])
          : (docsResp?.data ?? []);
        const documents: Document[] = docsArray.map(
          (doc: SharedDocResponse) => {
            return {
              id: String(doc.id),
              name: doc.documentName || doc.fileName,
              size: doc.documentSize,
              type: "wage-slip",
              uploadedDate: formatDate(doc.lastUpdateDate),
              raw: doc,
            };
          },
        );

        setCompanies((prev) => [
          ...prev,
          {
            declarationData: undefined,
            id: String(response.data.id),
            name: sharingCompany.lender,
            reference: sharingCompany.accountNumber,
            type: "all",
            address: "",
            documents,
            selectedDocuments: [],
            lastShared: "",
            raw: response.data,
            isFp: false,
            declarationStatus: "not-completed",
          },
        ]);
      } catch (error) {
        femaCapture("creating_share_company_data_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
      }
    }
  }

  async function handleSave() {
    setDisableContinue(true);
    
    // selected support partners
    for (const company of selectedSupportPartners) {
      const sharingCompany = buildCompanyData(company);
      const resolvedCustomerId: string | number | null = await getCustomerId();

      try {
        const payload = {
          accountNumber: sharingCompany.accountNumber,
          customerId: resolvedCustomerId,
          isSharing: "Y",
          lenderName: sharingCompany.lender,
          schemaName: sharingCompany.schemaName,
          subDomain: sharingCompany.subDomain,
          shareNow: "Y",
          source: "additional-support",
        };

        const response = await httpPost<CustomerSharingResponse>(
          sharePageEndpoints.createCustomerSharing(),
          payload,
        );

        setCompanyShareLastUpdateDate(
          new Date(response.data.lastUpdateDate).getTime(),
        );

        const docsResp = await getCompanyDocs(
          String(response.data.id),
          response.data.lenderName,
          response.data.accountNumber,
        );
        const docsArray: SharedDocResponse[] = Array.isArray(docsResp)
          ? (docsResp as SharedDocResponse[])
          : (docsResp?.data ?? []);
        const documents: Document[] = docsArray.map(
          (doc: SharedDocResponse) => {
            return {
              id: String(doc.id),
              name: doc.documentName || doc.fileName,
              size: doc.documentSize,
              type: "wage-slip",
              uploadedDate: formatDate(doc.lastUpdateDate),
              raw: doc,
            };
          },
        );

        setCompanies((prev) => [
          ...prev,
          {
            declarationData: undefined,
            id: String(response.data.id),
            name: sharingCompany.lender,
            reference: sharingCompany.accountNumber,
            type: "all",
            address: "",
            documents,
            selectedDocuments: [],
            lastShared: "",
            raw: response.data,
            isFp: false,
            declarationStatus: "not-completed",
          },
        ]);
      } catch (error) {
        setDisableContinue(false);
        femaCapture("creating_share_company_data_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
        throw error;
      }
    }

    // for support company cards
    await sendSupportCompanies();

    onClose();
  }

  // GET postcode by region
  useEffect(() => {
    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    if (!customerData.postCode) {
      setPostcodeRegion("England");
      return;
    }

    const postcodeDistrict = (customerData.postCode || "")
      .toLowerCase()
      .replace(" ", "")
      .slice(0, -3)
      .toUpperCase()
      .trim();

    async function getPostcodeRegion(): Promise<ServiceResponse<any[]>> {
      try {
        const response = await httpGet<ApiResponse>(
          onboardingEndpoints.getPostcodeRegion(postcodeDistrict),
        );
        return response.data;
      } catch (error) {
        femaCapture("fetching_postcode_region_data_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
        throw error;
      }
    }

    getPostcodeRegion().then((res: any) => {
      setPostcodeRegion(res[0].postcodeRegion);
    });
  }, []);

  const hasFetchedClientsByPcode = useRef(false);

  // GET clients by postcode
  useEffect(() => {
    if (!loadedDebts || !dependantDobDataLoaded || !loadedBenefitsAmount) {
      return;
    }

    if (hasFetchedClientsByPcode.current) return;
    hasFetchedClientsByPcode.current = true;

    const rawCustomerData = localStorage.getItem("customerData");
    if (!rawCustomerData) {
      return;
    }

    let customerData: Record<string, unknown> | null = null;
    try {
      customerData = JSON.parse(rawCustomerData) as Record<string, unknown>;
    } catch {
      customerData = null;
    }

    const rawPostcode =
      (customerData?.postCode as string | undefined) ??
      (customerData?.postcode as string | undefined) ??
      "";

    const postcodeTrimmed = rawPostcode
      .toString()
      .toLowerCase()
      .replace(/\s+/g, "");

    async function getClientByPostcode(): Promise<ServiceResponse<any[]>> {
      try {
        const response = await httpGet<ApiResponse>(
          onboardingEndpoints.getPostcodesByPostCode(postcodeTrimmed),
        );
        return response.data;
      } catch (error) {
        femaCapture("fetching_postcodes_data_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
        throw error;
      }
    }

    getClientByPostcode().then(async (res: any) => {
      if (!res || !Array.isArray(res)) return;

      const enw = res.some((i) => i.clientName === "ENW");
      const ukpn = res.some((i) => i.clientName === "UKPN");
      const westView = res.some(
        (i) => i.clientName === "West View Advice and Resource Centre",
      );
      const citizens = res.some(
        (i) => i.clientName === "Citizens Advice Mid Lincolnshire",
      );
      const wellness = res.some((i) =>
        ["Bristol Water", "Wessex Water"].includes(i.clientName),
      );
      const uuWellness = res.some((i) => i.clientName === "United Utilities");
      const showCitizensAdvicePlymouth =
        rawPostcode.toString().toLowerCase().startsWith("pl") &&
        debts.length > 0;

      setShowENWMessage(enw);
      setShowUKPNMessage(ukpn);
      setShowWestViewAdviceAndResourceCentre(westView);
      setIsCitizensAdviceMidLincolnshire(citizens);
      setIsWellnessRelatedPostCode(wellness);
      setIsUUWellnessRelatedPostCode(uuWellness);
      setShowCitizensAdvicePlymouth(showCitizensAdvicePlymouth);
      setIsLoadedPcode(true);
    });
  }, [
    debts,
    loadedDebts,
    dependantDobData,
    dependantDobDataLoaded,
    loadedBenefitsAmount,
  ]);

  useEffect(() => {
    let isMounted = true;

    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    const fetchInbestData = async () => {
      try {
        const resp = await BenefitsService.startCheck(customerData.customerId, {
          skipErrorCapture: true,
        });

        if (!isMounted || !resp?.success || !resp.data) return;

        const {
          extraMonthlyBenefitsAmount,
          waterSupportEntitlement,
          waterSureEntitlement,
          providerName,
        } = resp.data;

        localStorage.setItem(
          "BENEFITS_AMOUNT",
          String(extraMonthlyBenefitsAmount ?? 0),
        );
        setLoadedBenefitsAmount(true);

        setWaterSupportEntitlement(waterSupportEntitlement as string);
        setwaterSureEntitlement(waterSureEntitlement as string);
        setWaterCompany(providerName as string);
        setExtraMonthlyBenefitsAmount(extraMonthlyBenefitsAmount);
        setIsLoadedWater(true);
      } catch (e) {
        console.warn("Failed to fetch inbest data:", e);
        setLoadedBenefitsAmount(true);
        setIsLoadedWater(true);

        femaCapture("fetching_inbest_data_error", e, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
      }
    };

    fetchInbestData();

    return () => {
      isMounted = false;
    };
  }, []);

  const hasFetchedDebts = useRef(false);

  // GET debts
  useEffect(() => {
    if (hasFetchedDebts.current) return;
    hasFetchedDebts.current = true;

    async function getDebts() {
      const resolvedCustomerId: string | number | null = await getCustomerId();
      const debts = await OnboardingService.getDebts(
        Number(resolvedCustomerId),
        {
          skipErrorCapture: true,
        },
      );
      return debts;
    }

    getDebts().then((debts) => {
      setDebts(debts);
      setLoadedDebts(true);
    });
  }, []);

  // GET Creditor List Names
  useEffect(() => {
    async function getCreditorNames(): Promise<DigitalLender[]> {
      return digitalLenders;
    }

    getCreditorNames().then((data: any) => {
      setCreditorNamesList(data);
    });
  }, [digitalLenders]);

  async function sendDebtAdvice(notificationSource: string) {
    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    let companyName = customerData.field5TxtFld;

    if (
      companyName === "I have not been referred by a company" &&
      isWellnessRelatedPostCode
    ) {
      companyName = "Bristol Water";
    }

    const payload: NotifyCustomerDebtAdvice = {
      companyName,
      userId: customerData.userId,
      isUnitedUtilitiesCompany: "N",
      mobileNumber: customerData.mobileNumber,
      notificationSource,
    };

    const resp = await httpPost<NotifyCustomerDebtAdviceResponse>(
      customerEndpoints.notifyCustomerDebtAdvice(),
      payload,
    );

    const msg = resp.data.notifyInformation;

    showAlert({
      severity: msg.toLowerCase().includes("fail") ? "error" : "success",
      message: msg,
      autoHide: true,
      duration: 2000,
    });

    setDisableMwgBtns(true);
    setCount(30);
    setVisible(true);
  }

  function isUUEligibleForMoneyWellNess(field5TxtFld: string) {
    // const isWaterCompanyExist = debts.some((item: Debt) =>
    //   ["WA", "WS"].includes(item.accTypeCode)
    // );

    const priorityDebts = debts.filter(
      (item: Debt) =>
        item.debtType.replace("1.", "").trim() === "Priority Debts",
    );

    const nonPriorityDebts = debts.filter(
      (item: Debt) =>
        item.debtType.replace("2.", "").trim() === "Non Priority Debts",
    );

    const priorityDebtsLength = priorityDebts.filter(
      ({ accTypeCode }) => !["WA", "WS"].includes(accTypeCode),
    ).length;
    const nonPriorityDebtsLength = nonPriorityDebts.filter(
      ({ accTypeCode }) => !["WA", "WS"].includes(accTypeCode),
    ).length;
    const waterDebtsLength = nonPriorityDebts.filter(({ accTypeCode }) =>
      ["WA", "WS"].includes(accTypeCode),
    ).length;

    // Product spec – MWG SIGN POST eligibility:
    // 1. Referral = United Utilities AND any of:
    //    a) >= 1 priority AND >= 1 water debt (non-priority WA/WS)
    //    b) > 2 priority debts (i.e. >= 3)
    //    c) exactly 2 non-priority debts with water debt
    //    d) >= 3 non-priority debts with water debt
    // 2. Same debt conditions when referral is
    //    "I have not been referred by a company" OR
    //    "I can't find my referred company with United Utilities postcode"
    //    AND postcode is flagged as UU wellness related.

    const hasPriorityAndWater =
      priorityDebtsLength == 1 && waterDebtsLength == 1;
    const hasMoreThanTwoPriority = priorityDebtsLength >= 2;
    const hasTwoNonPriorityWithWater =
      nonPriorityDebtsLength === 2 && waterDebtsLength >= 1;
    const hasThreeOrMoreNonPriorityWithWater = nonPriorityDebtsLength >= 4;

    const meetsDebtThresholds =
      hasPriorityAndWater ||
      hasMoreThanTwoPriority ||
      hasTwoNonPriorityWithWater ||
      hasThreeOrMoreNonPriorityWithWater;

    const isUUReferral = field5TxtFld === "United Utilities";
    const isNotReferred =
      field5TxtFld === "I have not been referred by a company";
    const isCannotFindUUCompany =
      field5TxtFld ===
      "I can't find my referred company with United Utilities postcode";

    const isUUCompanyAndEligible = isUUReferral && meetsDebtThresholds;

    const isUnreferredWithUUPostcodeAndEligible =
      (isNotReferred || isCannotFindUUCompany) &&
      isUUWellnessRelatedPostCode &&
      meetsDebtThresholds;

    if (isUUCompanyAndEligible || isUnreferredWithUUPostcodeAndEligible) {
      return true;
    }

    return false;
  }

  const hasFetchedTotalEnergyBills = useRef(false);

  // GET total energy bills
  useEffect(() => {
    if (hasFetchedTotalEnergyBills.current) return;
    hasFetchedTotalEnergyBills.current = true;

    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    async function getTotalEnergyBills(): Promise<ServiceResponse<any[]>> {
      try {
        const response = await httpGet<ApiResponse>(
          customerEndpoints.getTotalEnergyBills(customerData.customerId),
        );
        return response.data;
      } catch (error) {
        femaCapture("fetching_energy_bills_data_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
        throw error;
      }
    }

    getTotalEnergyBills().then((res: any) => {
      setTotalEnergyBills(res[0]);
    });
  }, []);

  // GET customer share data
  useEffect(() => {
    if (!digitalData) {
      return;
    }

    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    async function getCustomerShareData() {
      const data: any = await SharePageService.getCustomerSharing(
        customerData.id,
      );

      setCustomerShareData(data);

      // Set initial toggles for all cards
      const useStateMap: Record<string, (...args: any[]) => void> = {
        mlb: setSelectedMlb,
        im: setSelectedIm,
        vrs: setSelectedVrs,
        sls: setSelectedSls,
        ndl: setSelectedNdl,
        pp: setSelectedPp,
        cg: setSelectedCg,
        ye: setSelectedYe,
        ywct: setSelectedYwct,
      };

      const reverseLenderNameMap: Record<string, string> = {
        "Make Life Better": "mlb",
        "Income Max": "im",
        "Vulnerability Registration Service": "vrs",
        "Stop Loan Sharks": "sls",
        "National Debtline": "ndl",
        PayPlan: "pp",
        "Cadent Gas": "cg",
        "YES Energy Solutions": "ye",
        "Yorkshire Water Community Trust": "ywct",
      };

      for (const entry of data) {
        if (entry.isDeleted == "Y" || entry.isSharing == "N") {
          continue;
        }

        const company = entry.lenderName;
        if (reverseLenderNameMap.hasOwnProperty(company)) {
          const key = reverseLenderNameMap[company] as string;
          useStateMap[key]?.(entry.shareNow);
        }
      }

      return;
    }

    getCustomerShareData();
  }, [digitalData]);

  const hasFetchedAdditionalSupportCompanies = useRef(false);

  const hasDependantUnder5 = useCallback(async (): Promise<boolean> => {
    return dependantDobData.some((dep) => {
      const ageYears = calculateAge(
        dep.childDependantDobStr ?? dep.childDependantDob,
      );
      return typeof ageYears === "number" && ageYears < 5;
    });
  }, [dependantDobData]);

  const hasDependantOver5 = useCallback(async (): Promise<boolean> => {
    return dependantDobData.some((dep) => {
      const ageYears = calculateAge(
        dep.childDependantDobStr ?? dep.childDependantDob,
      );
      return typeof ageYears === "number" && ageYears > 5;
    });
  }, [dependantDobData]);

  // GET additional support companies
  useEffect(() => {
    if (!dependantDobDataLoaded || !loadedBenefitsAmount || !isLoadedWater) {
      return;
    }

    if (hasFetchedAdditionalSupportCompanies.current) return;
    hasFetchedAdditionalSupportCompanies.current = true;

    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    async function getAdditionalSupportCompanies(): Promise<
      ServiceResponse<any[]>
    > {
      try {
        const queryParams = [
          { param: "customerId", value: customerData.customerId },
          {
            param: "extraBenefitAmount",
            value:
              extraMonthlyBenefitsAmount ||
              localStorage.getItem("BENEFITS_AMOUNT") ||
              "0",
          },

          {
            param: "childrenAgeUnderFive",
            value: (await hasDependantUnder5()) ? "Y" : "N",
          },
          {
            param: "customerHasPensionAge",
            value: isOver65(customerData.debtor1DateOfBirthStr) ? "Y" : "N",
          },
          { param: "jointAccount", value: customerData.jointAccount || "N" },
          {
            param: "dependentChildrenUnder16",
            value: customerData.dependentChildrenUnder16 || 0,
          },
          {
            param: "dependentchildrenaged16plus",
            value: customerData.dependentChildrenAged16Plus || 0,
          },
          { param: "postCode", value: customerData.postcodeTrimmed },
          {
            param: "vulnerabilityConsent",
            value:
              (String(
                customerData.field2TxtFld || customerData.field2Txtfld || "",
              ).toUpperCase() === "Y" ||
                String(
                  customerData.field2TxtFld || customerData.field2Txtfld || "",
                ).toUpperCase() === "YES") &&
              coerceConsentToHuman(customerData.field4TxtFld) === "Yes"
                ? "Yes"
                : "No",
          },
          {
            param: "referredCompany",
            value: customerData.field5TxtFld,
          },
        ];

        const response = await httpGet<ApiResponse>(
          // isOver65
          customerEndpoints.showAdditionalSupportCompanies() +
            buildQueryString(queryParams),
        );

        return response.data;
      } catch (error) {
        femaCapture("fetching_additional_support_data_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
        throw error;
      }
    }

    getAdditionalSupportCompanies().then((res: any) => {
      setAdditionalSupportCompanies(res);
    });
  }, [
    dependantDobData,
    dependantDobDataLoaded,
    loadedBenefitsAmount,
    isLoadedWater,
  ]);

  const hasFetchedIeMstrLenderSupporterNotShared = useRef(false);

  // GET getIeMstrLenderSupporterNotShared companies
  useEffect(() => {
    if (hasFetchedIeMstrLenderSupporterNotShared.current) return;
    hasFetchedIeMstrLenderSupporterNotShared.current = true;

    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    async function getIeMstrLenderSupporterNotShared(): Promise<
      ServiceResponse<any[]>
    > {
      try {
        const response = await httpGet<ApiResponse>(
          customerEndpoints.listNotSharedLenders(
            Number(customerData.customerId),
          ),
        );

        return response.data;
      } catch (error) {
        femaCapture("fetching_lender_support_data_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
        throw error;
      }
    }

    getIeMstrLenderSupporterNotShared().then((res: any) => {
      setSupportPartnersNames(
        res.map((item: LenderNotShared) => item.description),
      );
    });
  }, []);

  // NDL Condition
  useEffect(() => {
    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    const isNationalDebtlineMWN =
      ["Wessex Water", "Bristol Water"].includes(customerData.field5TxtFld) &&
      debts.length >= 1;

    setIsNationalDebtlineMWN(isNationalDebtlineMWN);
  }, [debts, isWellnessRelatedPostCode]);

  // MWG Condition
  useEffect(() => {
    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    const isOfferMoneyWellness =
      isUUEligibleForMoneyWellNess(customerData.field5TxtFld) ||
      customerData.field5TxtFld == "Roots to Froots";

    setIsOfferMoneyWellness(isOfferMoneyWellness);
  }, [debts, creditorNamesList, isUUWellnessRelatedPostCode]);

  // YES ENERGY Condition
  useEffect(() => {
    if (!postcodeRegion && !summary && !totalEnergyBills) {
      return;
    }

    const region = postcodeRegion;
    const totalEnergyData =
      totalEnergyBills.totalExpEnergyBills +
      totalEnergyBills.totalDebtRepayEnergyBills;

    const totalOutgoingsFixed =
      Math.round(summary.totalOutgoingsFixed * 100) / 100;
    const totalOutgoingsFlexible =
      Math.round(summary.totalOutgoingsFlexible * 100) / 100;
    const totalRepaymentDebts =
      Math.round(summary.totalRepaymentDebts * 100) / 100;

    const totalExpenditure =
      totalOutgoingsFixed + totalOutgoingsFlexible + totalRepaymentDebts;
    const percentageEnergyBills = (totalEnergyData / totalExpenditure) * 100;
    const showYesEnergyCard =
      percentageEnergyBills >= 15 &&
      (region == "England" || region == "Scotland");

    setShowYesEnergy(showYesEnergyCard);
  }, [summary, totalEnergyBills, postcodeRegion]);

  // YWCT Condition
  useEffect(() => {
    const YW = "Yorkshire Water";

    const isYorkshireWaterDebt = debts.some(
      ({ creditor, accTypeCode, amountOwed }) =>
        creditor === YW &&
        ["WA", "WS"].includes(accTypeCode) &&
        amountOwed >= 50 &&
        amountOwed <= 1500,
    );

    const hasValidPriorityDebt = debts.some(
      ({ debtType, accTypeCode, amountOwed }: Debt) =>
        (accTypeCode == "HT" ||
          (debtType.replace("1.", "").trim() === "Priority Debts" &&
            ["HR", "MG", "CT", "HT", "GA", "EE", "OF"].includes(
              accTypeCode,
            ))) &&
        amountOwed >= 50,
    );

    // show support message
    const showYwctCard = isYorkshireWaterDebt && hasValidPriorityDebt;

    setShowYWCTMessage(showYwctCard);
  }, [debts]);

  // Build support card messages
  useEffect(() => {
    const waterSupportQualified =
      (waterSupportEntitlement == "Y" || waterSureEntitlement == "Y") &&
      (waterCompany != undefined || waterCompany != "");

    const recommendationCompanies = waterSupportQualified ? waterCompany : "";

    const messages = {
      showENWMessage: "Electricity North West",
      showUKPNMessage: "UK Power Networks",
      showWestViewAdviceAndResourceCentre:
        "West View Advice and Resource Centre",
      showCitizensAdviceMidLincolnshire: "Citizens Advice Mid Lincolnshire",
      showYWCTMessage: "Yorkshire Water Community Trust",
      showCitizensAdvicePlymouth: "Citizens Advice Plymouth",
    };

    const utilityMessages = [];

    if (showENWMessage) utilityMessages.push(messages.showENWMessage);
    if (showUKPNMessage) utilityMessages.push(messages.showUKPNMessage);
    if (showWestViewAdviceAndResourceCentre)
      utilityMessages.push(messages.showWestViewAdviceAndResourceCentre);
    if (isCitizensAdviceMidLincolnshire)
      utilityMessages.push(messages.showCitizensAdviceMidLincolnshire);
    if (showYWCTMessage) utilityMessages.push(messages.showYWCTMessage);
    if (showCitizensAdvicePlymouth)
      utilityMessages.push(messages.showCitizensAdvicePlymouth);

    let companyArr: string[] = [...utilityMessages];

    if (recommendationCompanies) {
      companyArr.push(recommendationCompanies);
    }

    setCompanyArr(companyArr);
  }, [isLoadedWater, isLoadedPcode]);

  // SLS Condition
  useEffect(() => {
    const hasFriendAndColleagueDebt = debts.some(
      (debt: Debt) => debt.accTypeCode && debt.accTypeCode === "FC",
    );

    const isEnglandPostcode = postcodeRegion.toLowerCase() == "england";

    const showSlsCard = hasFriendAndColleagueDebt && isEnglandPostcode;

    setIsSLSEligible(showSlsCard);
  }, [debts, postcodeRegion]);

  const aspLoaded = useCallback(() => {
    return isLoadedPcode && isLoadedWater && loadedDebts;
  }, [isLoadedPcode, isLoadedWater, loadedDebts]);

  // VRS  Condition
  useEffect(() => {
    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string,
    );

    const validField3 = ["MySelf", "PartyInMyBudget", "BothInMyBudget"];

    const showVrsCard =
      validField3.includes(customerData.field3TxtFld || "None") &&
      isConsentAffirmative(customerData.field4TxtFld);

    setHasVulnerabilityRegistrationService(showVrsCard);
  }, [debts]);

  // MLB, Income Max, PayPlan  Condition
  useEffect(() => {
    const showMLBCard = additionalSupportCompanies.some(
      (company) => company.companyName === "Make Life Better",
    );
    const showIncomeMaxCard = additionalSupportCompanies.some(
      (company) => company.companyName === "Income Max",
    );

    const shouldShowPayPlan = additionalSupportCompanies.some(
      (company) => company.companyName === "PayPlan",
    );

    const isCadentEligible = additionalSupportCompanies.some(
      (company) => company.companyName === "Cadent Gas",
    );

    setShowMLBCard(showMLBCard);
    setShowIncomeMaxCard(showIncomeMaxCard);
    setShouldShowPayPlan(shouldShowPayPlan);
    setIsCadentEligible(isCadentEligible);
  }, [debts, additionalSupportCompanies]);

  type RenderSupportCompanyCardProps = {
    title: string;
    titleMeta?: string;
    subtitle: string;
    description: string;
    bulletPoints?: string[];
    selectedValue?: "Y" | "N" | string;
    handleShareCompany?: (value: "Y" | "N", companyKey: string) => void;
    companyKey?: string;
    showYesNo?: boolean;
    description2?: string;
    ctaText?: string;
  };

  function renderSupportCompanyCard({
    title,
    titleMeta,
    subtitle,
    description,
    bulletPoints = [],
    description2,
    selectedValue,
    handleShareCompany,
    companyKey,
    showYesNo,
    ctaText,
  }: RenderSupportCompanyCardProps) {
    return (
      <Box>
        {/* Title */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 0 }}>
          {title}
        </Typography>

        {/* Subtitle */}
        <Typography
          variant="h6"
          sx={{ fontWeight: 400, mb: 1, color: ThemeColours.subtitleVar2 }}
        >
          {subtitle}
        </Typography>

        {/* Description */}
        <Box sx={{ mb: 1 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 400, mb: 1, color: ThemeColours.bodyVar1 }}
          >
            {description}
          </Typography>
        </Box>

        {/* Bullet Points */}
        <Box sx={{ mb: 1 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {bulletPoints.map((item, index) => (
              <Box
                key={index}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <CheckIcon sx={{ color: "green", fontSize: 12 }} />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 400,
                    mb: 0,
                    color: ThemeColours.bodyVar1,
                  }}
                >
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {description2 && (
          <Box sx={{ mb: 0 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 400, mb: 0, color: ThemeColours.bodyVar1 }}
            >
              {description2}
            </Typography>
          </Box>
        )}

        {/* Share Section */}
        {showYesNo && (
          <Box
            sx={{
              backgroundColor: "#EFF6FF",
              border: "1px solid #BFDBFE",
              borderRadius: 2,
              p: 2,
              mb: 0,
              mt: 1,
              display: "flex",
              gap: 1,
            }}
          >
            <Typography
              variant="body1"
              sx={{ color: "#1E40AF", lineHeight: 1.6 }}
            >
              {ctaText
                ? ctaText
                : `Would you like us to share your budget with ${titleMeta || title}?`}
              <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                <Button
                  variant="contained"
                  onClick={() => {
                    if (!handleShareCompany || !companyKey) return;
                    handleShareCompany("Y", companyKey);
                  }}
                  sx={{
                    backgroundColor:
                      selectedValue === "Y" ? ThemeColours.azul : "#E5E7EB",
                    color: selectedValue === "Y" ? "white" : "black",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor:
                        selectedValue === "Y" ? ThemeColours.azul : "#D1D5DB",
                      boxShadow: "none",
                    },
                    fontWeight: 400,
                  }}
                >
                  Yes
                </Button>

                <Button
                  variant="contained"
                  onClick={() => {
                    if (!handleShareCompany || !companyKey) return;
                    handleShareCompany("N", companyKey);
                  }}
                  sx={{
                    backgroundColor:
                      selectedValue === "N" ? ThemeColours.azul : "#E5E7EB",
                    color: selectedValue === "N" ? "white" : "black",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor:
                        selectedValue === "N" ? ThemeColours.azul : "#D1D5DB",
                      boxShadow: "none",
                    },
                    fontWeight: 400,
                  }}
                >
                  No
                </Button>
              </Box>
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  const themeSelectRef = useRef<{ open: () => void; close: () => void } | null>(
    null,
  );

  const handleAutocompleteClick = () => {
    themeSelectRef.current?.open?.();
  };

  const cards = [
    {
      id: "Support Card",
      show: companyArr.length > 0,
      content: (
        <>
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 400,
              mb: 1,
            }}
          >
            These companies have been added to your share list to make things
            easier. If you don’t want to share your budget with a company, you
            can remove it on the next screen.
          </Typography>

          {companyArr.map((company, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: index < companyArr.length - 1 ? 2 : 0,
              }}
            >
              <DotIcon sx={{ fontSize: 8, color: "#00243e" }} />
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: "#00243e",
                }}
              >
                {company}
              </Typography>
            </Box>
          ))}
        </>
      ),
    },
    {
      id: "NDL",
      show: isNationalDebtlineMWN,
      content: renderSupportCompanyCard({
        title: "National Debtline",
        subtitle: "Support from National Debtline",
        description:
          "National Debtline provides free and independent debt advice to help you make a clear plan to manage what you owe.",
        bulletPoints: [
          "Free independent debt advice",
          "Clear steps to manage debts",
          "Practical guidance that works",
        ],
        description2:
          "With your consent we’ll share your budget with National Debtline so they can offer you support.",
        selectedValue: selectedNdl,
        handleShareCompany,
        companyKey: "ndl",
        showYesNo: true,
      }),
    },
    {
      id: "MWG SIGN POST",
      show: isOfferMoneyWellness,
      content: (
        <>
          {renderSupportCompanyCard({
            title: "Money Wellness",
            subtitle: "Support from Money Wellness",
            description:
              "Appointment service to get free, one-to-one support from a specialist advisor. They’ll take the time to understand your situation and create a clear plan to help you manage your debts and improve your financial wellbeing.",
            bulletPoints: [
              "Tailored debt advice that fits your situation",
              "Non-judgmental guidance from trained experts",
              "Tools and tips to help you better manage your finances",
            ],
            showYesNo: false,
          })}

          <Divider sx={{ mt: 2, mb: 2.5 }} />

          <Typography
            variant="h6"
            sx={{ fontWeight: 400, mb: 2, color: ThemeColours.subtitleVar2 }}
          >
            To learn more, please select from the available contact option(s)
            below.{" "}
            <span style={{ color: "red" }}>
              Note: You will only see the “Text me” option if you have entered a
              mobile number as part of the onboarding below.
            </span>
          </Typography>

          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              onClick={() => {
                sendDebtAdvice("sendEmail");
              }}
              size="medium"
              sx={{
                borderRadius: 1,
                textTransform: "none",
                fontWeight: 400,
                py: 1,
                px: 3,
                boxShadow: "none",
                backgroundColor: ThemeColours.buttonVar1,
              }}
              disabled={disableMwgBtns}
            >
              Email Me
            </Button>
            {hasMobileNumber && (
              <Button
                variant="contained"
                size="medium"
                sx={{
                  borderRadius: 1,
                  textTransform: "none",
                  fontWeight: 400,
                  py: 1,
                  px: 3,
                  boxShadow: "none",
                  backgroundColor: ThemeColours.buttonVar1,
                }}
                onClick={() => {
                  sendDebtAdvice("sendSMS");
                }}
                disabled={disableMwgBtns}
              >
                Text Me
              </Button>
            )}
          </Box>
          <Box>
            {visible && (
              <Typography
                variant="h6"
                sx={{
                  color: "#6B7280",
                  fontWeight: 400,
                  fontSize: "14px",
                  lineHeight: "21px",
                }}
              >
                Resend in {count} {count === 1 ? "second" : "seconds"}.
              </Typography>
            )}
          </Box>
        </>
      ),
    },
    {
      id: "YES ENERGY",
      show: showYesEnergy,
      content: renderSupportCompanyCard({
        title: "YES Energy Solutions",
        subtitle: "Support from YES Energy Solutions",
        description:
          "Help to cut energy bills with simple, tailored guidance and access to the right support schemes.",
        bulletPoints: [
          "Tailored energy-saving advice",
          "Help to find better tariffs",
          "Support to reduce ongoing costs",
        ],
        description2:
          "With your consent we’ll share your budget with YES Energy Solutions so they can offer you support.",
        selectedValue: selectedYe,
        handleShareCompany,
        companyKey: "ye",
        showYesNo: false,
      }),
    },
    {
      title: "YWCT - (YORKSHIRE WATER COMMUNITY TRUST)",
      show: showYWCTMessage,
      content: renderSupportCompanyCard({
        title: "Yorkshire Water Community Trust",
        subtitle: "Support from Yorkshire Water Community Trust",
        description:
          "Practical help for customers struggling with water bills, with schemes designed to make payments more manageable.",
        bulletPoints: [
          "Help with water bill debt",
          "Grants and payment support",
          "Quick, simple applications",
        ],
        description2:
          "With your consent we’ll share your budget with Yorkshire Water Community Trust so they can offer you support.",
        selectedValue: selectedYwct,
        handleShareCompany,
        companyKey: "ywct",
        showYesNo: false,
      }),
    },
    // { title: "ENW AS REFERRED COMPANY", show: showEnwReferredCard },
    {
      title: "SLS",
      show: isSLSEligible,
      content: renderSupportCompanyCard({
        title: "Illegal Money Lending Team (Stop Loan Sharks)",
        subtitle: "Support from Illegal Money Lending Team (Stop Loan Sharks)",
        description:
          "Confidential emotional and practical support to anyone affected by illegal money lending. The Stop Loan Sharks team can help keep you safe and take action against loan sharks.",
        bulletPoints: [
          "Free and confidential support if you’ve borrowed from a loan shark",
          "Emotional and practical help from trained staff",
          "Tailored support to keep you safe and in control",
        ],
        description2:
          "With your consent we’ll share your budget with Illegal Money Lending Team (Stop Loan Sharks) so they can offer you support.",
        selectedValue: selectedSls,
        handleShareCompany,
        companyKey: "sls",
        showYesNo: true,
      }),
    },
    // { title: "ENW RECOMMENDATION", show: showEnwRecommendationCard },
    // { title: "SIGNPOST+RECOMMENDATION", show: showSignpostRecommendationCard },
    {
      title: "Vulnerability Registration Service",
      show: hasVulnerabilityRegistrationService,
      content: renderSupportCompanyCard({
        title: "VRS",
        subtitle: "Support from VRS",
        description:
          "Free service that helps organisations understand when people need extra support, so they can treat them with care and understanding.",
        bulletPoints: [
          "Register your circumstances once",
          "Get tailored support when dealing with companies",
          "Free, secure and trusted service",
        ],
        description2:
          "With your consent we’ll share your budget with VRS so they can alert comanies of your vulnerable circumstances.",
        selectedValue: selectedVrs,
        handleShareCompany,
        companyKey: "vrs",
        showYesNo: true,
      }),
    },
    {
      title: "MLB (Make life better)",
      show: showMLBCard,
      content: renderSupportCompanyCard({
        title: "Make Life Better",
        subtitle: "Support from Make Life Better",
        description:
          "Help for families in financial hardship to unlock the benefits, grants and discounts they’re entitled to - without the stress of navigating complex systems.",
        bulletPoints: [
          "Support finding unclaimed benefits and grants",
          "Help accessing discounts and financial support",
          "Easy, friendly guidance throughout the process",
        ],
        description2:
          "With your consent we’ll share your budget with Make Life Better so they can offer you support.",
        selectedValue: selectedMlb,
        handleShareCompany,
        companyKey: "mlb",
        showYesNo: true,
        ctaText: `Would you like us to share your budget with Make Life Better? If you consent we'll also send you a link so you can book an appointment with the MLB team.`,
      }),
    },
    {
      title: "Income max",
      show: showIncomeMaxCard,
      content: renderSupportCompanyCard({
        title: "Income Max",
        subtitle: "Support from Income Max",
        description:
          "Help to identify and claim benefits and grants you’re entitled to, boosting household income.",
        bulletPoints: [
          "Personalised benefits check",
          "Help with claims and forms",
          "Support to boost household income",
        ],
        description2:
          "With your consent we’ll share your budget with Income Max so they can offer you support.",
        selectedValue: selectedIm,
        handleShareCompany,
        companyKey: "im",
        showYesNo: true,
        ctaText: `Would you like us to share your budget with Income Max to receive help making a claim?`,
      }),
    },
    {
      title: "PayPlan",
      show: shouldShowPayPlan,
      content: renderSupportCompanyCard({
        title: "PayPlan",
        subtitle: "Support from PayPlan",
        description:
          "PayPlan offers free, confidential debt advice and helps you create affordable, sustainable repayment plans.",
        bulletPoints: [
          "Free, confidential debt advice",
          "Affordable repayment plans",
          "Tools to get back in control",
        ],
        description2:
          "With your consent we’ll share your budget with PayPlan so they can offer you support.",
        selectedValue: selectedPp,
        handleShareCompany,
        companyKey: "pp",
        showYesNo: true,
        ctaText: `Would you like us to share your budget with PayPlan so you can receive debt advice?`,
      }),
    },
    {
      title: "CadentGas",
      show: isCadentEligible,
      content: renderSupportCompanyCard({
        title: "Free Gas Safety Check and Servicing Available",
        titleMeta: "Cadent",
        subtitle:
          "You may be entitled to free support to help keep your home safe and warm.",
        description:
          "Based on your current situation, an engineer can come to your property to carry out free gas appliance safety checks and services.\n This support is provided through Cadent’s ‘Services Beyond the Meter’ programme, which helps people who may be experiencing financial hardship.",
        bulletPoints: [
          "Free gas appliance safety checks and support",
          "Help for customers facing financial vulnerability",
          "Tailored help to make your home safer and more efficient",
        ],
        description2:
          "With your consent we’ll share your budget with Services Beyond the Meter (Cadent) so they can offer you support.",
        selectedValue: selectedCg,
        handleShareCompany,
        companyKey: "cg",
        showYesNo: true,
      }),
    },
    {
      title: "Additional Support Partners",
      show: true,
      content: (
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 500, mb: 1 }}>
            Additional Support Partners{" "}
          </Typography>
          <Box sx={{ mb: 1 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 400, mb: 1, color: ThemeColours.bodyVar1 }}
            >
              We work with trusted partners who might offer you additional
              support. Besides the companies you're sharing your budget with,
              you can also include others from the list below.
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 400, mb: 1, color: ThemeColours.subtitleVar3 }}
            >
              Do you wish to share your budget with any of our support partners?
            </Typography>
          </Box>
          <Box>
            <FormControl sx={{ minWidth: "100%" }}>
              <Autocomplete
                multiple
                options={supportPartnersNames}
                onOpen={handleAutocompleteClick}
                value={selectedSupportPartners}
                onChange={(_event, newValue) =>
                  setSelectedSupportPartners(newValue)
                }
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option}
                      {...getTagProps({ index })}
                      sx={{ backgroundColor: "#F2F5F6" }}
                    />
                  ))
                }
                renderInput={(params) => {
                  const { size, InputLabelProps, ...rest } = params;
                  const safeInputLabelProps = InputLabelProps
                    ? {
                        className: InputLabelProps.className ?? "",
                        style: InputLabelProps.style ?? {},
                      }
                    : null;

                  return (
                    <TextField
                      {...rest}
                      {...(size ? { size } : {})}
                      {...(safeInputLabelProps
                        ? { InputLabelProps: safeInputLabelProps }
                        : {})}
                      disabled
                      variant="outlined"
                      placeholder={
                        selectedSupportPartners.length === 0
                          ? "Select company(ies)"
                          : ""
                      }
                      InputProps={{
                        ...params.InputProps,
                        readOnly: true,
                        sx: {
                          padding: 0,
                          cursor: "pointer",
                          "& .MuiInputBase-input": {
                            cursor: "pointer",
                            caretColor: "transparent",
                            userSelect: "none",
                          },
                        },
                      }}
                      sx={{
                        cursor: "pointer",
                        "& .MuiOutlinedInput-root": {
                          cursor: "pointer",
                        },
                      }}
                    />
                  );
                }}
                disableCloseOnSelect={false}
                sx={{
                  width: "100%",
                  borderRadius: 3,
                  "& .MuiAutocomplete-inputRoot": {
                    padding: 0.5,
                  },
                }}
              />

              <div style={{ display: "none" }}>
                <ThemeSelect
                  ref={themeSelectRef}
                  dialogLabel="Select a Company"
                  btnLabel="Add"
                  options={[
                    ...supportPartnersNames.map((creditor) => ({
                      value: creditor,
                      label: creditor,
                    })),
                  ]}
                  value={selectedSupportPartners}
                  onChange={(value) =>
                    setSelectedSupportPartners(
                      Array.isArray(value) ? value.map(String) : [],
                    )
                  }
                  placeholder="Select a Company"
                  showErrorBelow={false}
                  searchable={true}
                  fullWidth
                  multiple
                />
              </div>
            </FormControl>
          </Box>
        </Box>
      ),
    },
  ];

  return (
     <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Dialog Title */}
      <DialogTitle
        sx={{
          px: 3,
          justifyContent: "space-between",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 500 }}>
          Additional Support Options
        </Typography>

        <IconButton
          aria-label="close"
          onClick={handleSave}
          sx={{
            color: "#9ca3af",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider sx={{ marginLeft: 3, marginRight: 4 }} />

      {/* Dialog Content */}
      <DialogContent
        ref={contentRef}
        sx={{
          pt: 3,
          px: 3,
          flexGrow: 1,
          overflowY: "auto",
          position: "relative",
        }}
      >
        {aspLoaded() && (
          <Box sx={{ pt: 0 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Based on your budget and what you've told us, we've found some
              support services that might help you.
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {cards.map(
                (card, index) =>
                  card.show && (
                    <Card
                      key={index}
                      sx={{
                        width: "100%",
                        boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                        border: "1px solid #e0e0e0",
                        borderRadius: 3,
                      }}
                    >
                      <CardContent>
                        {card.content ? (
                          card.content
                        ) : (
                          <Typography variant="subtitle1">
                            {card.title}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  ),
              )}
            </Box>
          </Box>
        )}

        {!aspLoaded() && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "80%",
            }}
          >
            <CircularProgress />
          </Box>
        )}

        <Box sx={{ minHeight: 75 }}></Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 4,
          py: 2,
          borderTop: "1px solid #e0e0e0",
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: "white",
          zIndex: 1,
          boxShadow: "0 -4px 6px rgba(0, 0, 0, 0.1)",
          opacity: isAtBottom ? 1 : 0,
          pointerEvents: isAtBottom ? "auto" : "none",
          transition: "opacity 0.2s",
          maxHeight: 75,
        }}
      >
        <Button
          variant="contained"
          onClick={handleSave}
          size="large"
          sx={{
            borderRadius: 1,
            textTransform: "none",
            fontWeight: 500,
            py: 1.3,
            px: 3.5,
            boxShadow: "none",
            backgroundColor: ThemeColours.buttonVar1,
          }}
          disabled={!aspLoaded() || disableContinue}
        >
          Continue
        </Button>
      </DialogActions>
    </Box>
  );
};

export default AdditionalSupportModal;
